import { motion } from 'framer-motion';
import { Crown, Scale } from 'lucide-react';
import { OMS_COLORS, tint } from '../omsDashTheme';
import { ENGINEER_STATE_META, type EngineerPerf, type EngineerState } from '../utils/performanceModel';
import ScoreGauge from './ScoreGauge';
import StatCount from './StatCount';
import './explorer.css';

/** One engineer-state vocabulary, one colour each — used everywhere. */
export const ENGINEER_STATE_COLOR: Record<EngineerState, { ink: string; fill: string }> = {
  available: { ink: '#1F5A47', fill: '#339274' },
  assigned: { ink: '#0B3B2E', fill: '#7BAF9B' },
  overloaded: { ink: '#9F1239', fill: '#9F1239' },
  inactive: { ink: '#5A6275', fill: '#5A6275' },
};

export function EngineerStateChip({ state }: { state: EngineerState }) {
  const palette = ENGINEER_STATE_COLOR[state];
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.08em]"
      style={{ color: palette.ink, backgroundColor: tint(palette.fill, 0.14) }}
    >
      {ENGINEER_STATE_META[state].label}
    </span>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

interface Props {
  rows: EngineerPerf[];
  deltaPhrase: string;
  compareIds: Set<string>;
  onToggleCompare: (id: string) => void;
  onOpen: (engineer: EngineerPerf) => void;
}

/**
 * Explorer Level 3 — the site-engineer leaderboard for one project.
 * Rank 1 wears the champagne ribbon (recognition is a feature); overloaded
 * engineers get a rebalance flag, not just an alarm. Full names + initials
 * avatars keep duplicate initials unambiguous.
 */
export default function EngineerLeaderboard({ rows, deltaPhrase, compareIds, onToggleCompare, onOpen }: Props) {
  return (
    <ul className="space-y-1.5">
      {rows.map((row, index) => {
        const isTop = index === 0 && row.score !== null && rows.length > 1;
        const selected = compareIds.has(row.id);
        return (
          <motion.li
            key={row.id}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.28 }}
            className={`group relative ${isTop ? 'perf-ribbon rounded-xl' : ''}`}
            style={isTop ? { border: '1px solid rgba(201,169,97,0.55)', backgroundColor: tint('#C9A961', 0.08) } : undefined}
          >
            <button
              type="button"
              data-perf-row
              onClick={() => onOpen(row)}
              aria-label={`Open detail for ${row.name}`}
              className="grid w-full grid-cols-[auto_auto_minmax(0,1.6fr)_auto_auto] items-center gap-3 rounded-xl border border-transparent px-2.5 py-2 text-left transition hover:border-[rgba(11,59,46,0.16)] hover:bg-[rgba(11,59,46,0.04)] focus-visible:ring-2 focus-visible:ring-[rgba(51,146,116,0.5)]"
            >
              <span className="flex w-6 justify-center">
                {isTop ? (
                  <Crown className="h-4 w-4" style={{ color: OMS_COLORS.champagneDeep }} aria-label="Top performer" />
                ) : (
                  <span className="text-sm font-extrabold tabular-nums" style={{ color: OMS_COLORS.caption }}>
                    {index + 1}
                  </span>
                )}
              </span>

              <span
                aria-hidden
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold"
                style={{ backgroundColor: tint('#1F5A47', 0.12), color: '#1F5A47', border: '1px solid rgba(11,59,46,0.18)' }}
              >
                {initialsOf(row.name)}
              </span>

              <span className="min-w-0">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-[13px] font-extrabold tracking-tight" style={{ color: OMS_COLORS.tealDark }}>
                    {row.name}
                  </span>
                  <EngineerStateChip state={row.state} />
                </span>
                <span className="mt-0.5 block truncate text-[10.5px] font-semibold" style={{ color: OMS_COLORS.caption }}>
                  {row.roleName || 'Site role'}
                  {' · '}
                  {row.daysSinceLastSubmit === null
                    ? 'never submitted'
                    : row.daysSinceLastSubmit === 0
                      ? 'submitted today'
                      : `last submit ${row.daysSinceLastSubmit}d ago`}
                  {row.projectCount > 1 && ` · ${row.projectCount} projects in scope`}
                </span>
              </span>

              {/* Score block — ring gauge only, never a bare numeral. */}
              <span className="border-l border-[rgba(11,59,46,0.12)] pl-3">
                <ScoreGauge
                  score={row.score}
                  name={row.name}
                  size={40}
                  delta={row.delta}
                  deltaPhrase={deltaPhrase}
                  align="end"
                />
              </span>

              {/* Counts block — divided from the score block by its own hairline. */}
              <span className="hidden flex-col items-end gap-1 border-l border-[rgba(11,59,46,0.12)] pl-3 sm:flex">
                <StatCount
                  label="Completed"
                  value={row.completed}
                  of={row.scheduled}
                  noun="tasks"
                  size="sm"
                  align="end"
                  ink="#1F5A47"
                />
                <StatCount
                  label="Open"
                  value={row.open}
                  noun="tasks"
                  qualifier={row.overdue > 0 ? `open · ${row.overdue} overdue` : 'open'}
                  size="sm"
                  align="end"
                  ink={row.overdue > 0 ? '#9F1239' : OMS_COLORS.ink}
                />
              </span>
            </button>

            <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
              {row.state === 'overloaded' && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(row);
                  }}
                  className="hidden items-center gap-1 rounded-lg px-2 py-1 text-[9px] font-extrabold uppercase tracking-wide transition hover:bg-[rgba(159,18,57,0.12)] group-focus-within:flex group-hover:flex"
                  style={{ color: '#9F1239', backgroundColor: 'rgba(250,247,238,0.95)', border: '1px solid rgba(159,18,57,0.3)' }}
                >
                  <Scale className="h-3 w-3" /> Rebalance
                </button>
              )}
              <label
                className={`items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                  selected ? 'flex' : 'hidden group-focus-within:flex group-hover:flex'
                }`}
                style={{ color: OMS_COLORS.champagneDeep, backgroundColor: 'rgba(250,247,238,0.95)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggleCompare(row.id)}
                  aria-label={`Compare ${row.name}`}
                  className="h-3 w-3 accent-[#264f3c]"
                />
                Compare
              </label>
            </span>
          </motion.li>
        );
      })}
    </ul>
  );
}
